import { Components } from '@mui/material/styles';
import { Theme } from '@mui/material';
import { getFocusRingStyle } from '@/src/components/focusring/focusring';

// make sure the value can be divided by 2
export const flowThumbWidth = 40;
export const flowThumbHeight = 20;

export const flowSlider: Components<Theme>['MuiSlider'] = {
  defaultProps: {
    // value properties
    min: 0,
    max: 100,
    step: 1,
    // display label with hover
    valueLabelDisplay: 'auto',
    // disable auto-generated marks, it is recommended to use ./getSliderMarks.ts function
    marks: false,
  },
  styleOverrides: {
    root: ({ theme, ownerState }) => ({
      display: 'block',
      borderRadius: '0',
      // 32px height
      padding: '13px 0',
      ...(ownerState.orientation === 'vertical'
        ? {
            width: '6px',
            height: '320px',
          }
        : {
            height: '6px',
          }),
      // disabled state
      '&.Mui-disabled': {
        '& .MuiSlider-rail': {
          color: theme.palette.disabled.primary,
        },
        '& .MuiSlider-track': {
          color: theme.palette.disabled.tertiary,
        },
        '& .MuiSlider-mark': {
          backgroundColor: theme.palette.mode === 'dark' ? theme.palette.onSurface.os900 : theme.palette.onSurface.os0,
          '&.MuiSlider-markActive': {
            backgroundColor:
              theme.palette.mode === 'dark' ? theme.palette.onSurface.os900 : theme.palette.onSurface.os0,
          },
        },
        '& .MuiSlider-thumb': {
          borderColor: theme.palette.disabled.tertiary,
        },
      },
    }),
    // whole rail bellow the thumb
    rail: ({ theme }) => ({
      borderRadius: '6px',
      opacity: '1',
      color: theme.palette.onSurface.os500,
      // add translate3d, in addition to translateY(-50%), to prevent overflowing borderRadius
      transform: 'translate3d(0px, -50%, 0px)',
    }),
    // track marking selected segment
    track: ({ theme }) => ({
      borderRadius: '6px',
      border: 'none',
      color: theme.palette.mode === 'dark' ? theme.palette.brand.secondary : theme.palette.brand.primary,
      // add translate3d, in addition to translateY(-50%), to prevent overflowing borderRadius
      transform: 'translate3d(0px, -50%, 0px)',
    }),
    // dot on the rail and track
    mark: ({ theme }) => ({
      height: '4px',
      width: '4px',
      borderRadius: '4px',
      opacity: '1',
      backgroundColor: theme.palette.mode === 'dark' ? theme.palette.onSurface.os900 : theme.palette.onSurface.os0,
      '&.MuiSlider-markActive': {
        backgroundColor: theme.palette.mode === 'dark' ? theme.palette.onSurface.os900 : theme.palette.onSurface.os0,
      },
    }),
    // pointer
    thumb: ({ theme, ownerState }) => ({
      ...(ownerState.orientation === 'vertical'
        ? {
            width: `${flowThumbHeight}px`,
            height: `${flowThumbWidth}px`,
          }
        : {
            width: `${flowThumbWidth}px`,
            height: `${flowThumbHeight}px`,
          }),
      borderRadius: `${flowThumbHeight}px`,
      backgroundColor: theme.palette.onSurface.os0,
      border: `1px solid ${theme.palette.onSurface.os900}`,
      // hitbox target
      '&:after': {
        width: `${flowThumbWidth}px`,
        height: `${flowThumbWidth}px`,
        borderRadius: '1000px',
      },
      // focus style
      ...getFocusRingStyle(theme, true, 1),
      // extra shadow around
      transition: 'box-shadow 0.3s ease-out',
      '&, &:hover, &.Mui-active': {
        boxShadow: `none`,
      },
      '&.Mui-focusVisible': {
        // extra white border
        boxShadow: `0 0 0 2px ${theme.palette.background.primary}`,
        // hide focus during dragging
        '&.Mui-active': {
          boxShadow: 'none',
          outline: `0px solid ${theme.palette.background.primary}`,
          '&:before': {
            boxShadow: 'none',
          },
        },
      },
    }),
    // label above thumb
    valueLabel: ({ theme }) => ({
      maxWidth: 'none',
      // increase space between thumb and tooltip
      top: '-12px',
      // style box
      backgroundColor: theme.palette.text.primary,
      color: theme.palette.background.primary,
      borderRadius: '2px',
      padding: '8px 12px',
      // custom font-size
      [theme.breakpoints.up('xs')]: {
        fontSize: '12px',
        lineHeight: '16px',
        fontWeight: '400',
      },
    }),
  },
};
